'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import {
  HEADER_ICON_ACTION_CLASS,
  HEADER_ICON_ACTION_SIZE,
} from '@umodeapporg/ui/components/Shell/headerControlStyles';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import CustomBadge from '@umodeapporg/ui/components/UI/CustomBadge';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import { useFila } from '@/domain/fila/hooks/useFila';
import { PERMISSION, TPermissionId } from '@/domain/permissions/constants/permissionIds';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { usePortalAcesso } from '@/domain/portal/hooks/usePortalAcesso';
import { authStore } from '@/infrastructure/store/authStore';

const FILA_PATH = '/fila';

/** The same ids that open the Fila: the counter is part of it. */
const FILA_VIEW_PERMISSIONS: ReadonlyArray<TPermissionId> = [PERMISSION.FILA_VIEW, PERMISSION.MATERIAIS_VIEW];

/**
 * Header shortcut to the attention queue, with the count of what CHANGED today as a badge:
 * o que o sistema reprogramou sozinho e o que foi decidido na fila, que e o que a dona do
 * produto quer ver de relance. O detalhe de cada mudanca esta na Inicio. The shell mounts
 * before the session exists, so the first load waits for the unified login.
 */
export function FilaTrigger() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { mudancasHoje, loadMudancasHoje } = useFila();
  const isAuthenticated = useStore(authStore, (s) => s.isAuthenticated);
  // The count belongs to the active account: switching accounts reloads it.
  const partnerId = useStore(authStore, (s) => s.user?.partnerId ?? '');
  // The queue is the factory's: a brand buyer gets neither the count nor a shortcut to it, and
  // neither does a role without the Fila (the shortcut would lead to a screen it cannot open).
  const { isStaff } = usePortalAcesso();
  const { canAny } = usePermissions();
  const podeVerFila = isStaff && canAny(FILA_VIEW_PERMISSIONS);

  useEffect(() => {
    if (!isAuthenticated || partnerId === '' || !podeVerFila) {
      return;
    }
    const run = async (): Promise<void> => {
      await loadMudancasHoje();
    };
    run();
  }, [isAuthenticated, partnerId, podeVerFila, loadMudancasHoje]);

  if (!podeVerFila) {
    return null;
  }

  const mudancas = mudancasHoje?.length ?? 0;

  return (
    <CustomBadge
      content={mudancas}
      tone="danger"
      invisible={mudancas === 0}
      className="right-0 top-0 h-3.5 min-w-3.5 ring-2 ring-muted"
    >
      <ControlledTooltip content={mudancas > 0 ? t('home.mudancas.contagem', { n: mudancas }) : t('nav.fila')}>
        <ControlledButton defaultHtmlButton onClick={() => router.push(FILA_PATH)} className={HEADER_ICON_ACTION_CLASS}>
          <MaterialSymbol icon="notification_important" size={HEADER_ICON_ACTION_SIZE} />
        </ControlledButton>
      </ControlledTooltip>
    </CustomBadge>
  );
}
